import React from 'react';

import { Stack } from '@appComponents/navigation';
import { useAuthState } from '@appFirebase';
import Main from './main';
import Login from './auth/Login';
import ForgotPassword from './auth/ForgotPassword';
import { CreateAccount, CreateCompany } from './auth/CreateAccount';
import { useCompanyData, useMyData } from '@appUtils/api';
import { VALID_SUBSCRIPTION_STATUSES } from '@appUtils/constants/subscriptions';
import { UserRole } from '@appUtils/tripConverter';
import Text from '@appComponents/Text';
import Dialog from '@appComponents/Dialog';
import Button from '@appComponents/Button';
import { ScreenLoader } from '@appComponents/ScreenLayout';
import Terms from './common/Terms';
import { WaitingRoom } from './auth/WaitingRoom';
import SubscriptionStack from './main/SubscriptionStack';
import { logOut } from '@appUtils/auth';

export default function Screens() {
  const [auth, isLoading] = useAuthState();

  if (isLoading) {
    return null;
  }

  const AppScreens = auth ? AuthenticatedScreens : UnauthenticatedScreens;

  return (
    <AppScreens>
      <Stack.Group>
        <Stack.Screen
          name="Terms"
          component={Terms}
          options={{ title: 'Terms and Conditions' }}
        />
        <Stack.Screen
          name="Privacy"
          component={Terms}
          options={{ title: 'Privacy Policy' }}
        />
      </Stack.Group>
    </AppScreens>
  );
}

const AuthenticatedScreens = ({ children }) => {
  const [user, userLoading] = useMyData();
  const [company, companyLoading] = useCompanyData();
  const inTrialCodePeriod = company?.trialCodeExpirationDate
    ? new Date(
        company.trialCodeExpirationDate.seconds * 1000 +
          company.trialCodeExpirationDate.nanoseconds / 1000000,
      ) > new Date()
    : false;
  const hasSubscription =
    company?.subscriptionId &&
    VALID_SUBSCRIPTION_STATUSES.includes(company?.subscriptionStatus);
  const companyActive = hasSubscription || inTrialCodePeriod;

  if (userLoading || companyLoading) {
    return <ScreenLoader />;
  }

  if (!user?.managementCompany?.ref || user?.archived) {
    return (
      <Stack.Navigator>
        <Stack.Group>
          <Stack.Screen
            name="Waiting to Join Company"
            component={WaitingRoom}
            options={{ headerShown: false }}
          />
        </Stack.Group>
      </Stack.Navigator>
    );
  }
  const hasWebAccess =
    !userLoading &&
    (user.role === UserRole.MANAGER || user.role === UserRole.EMPLOYEE);

  if (!hasWebAccess) {
    return <UnauthenticatedScreens showWarning={true} children={children} />;
  }

  if (companyActive) {
    return (
      <Stack.Navigator>
        <Stack.Group>
          {/*The Main screen is a Drawer Navigator that handles all our main app routes*/}
          <Stack.Screen
            name="Main"
            component={Main}
            options={{ headerShown: false }}
          />
          {/*Non-drawer screen like modals can be added here*/}
        </Stack.Group>
        {children}
      </Stack.Navigator>
    );
  }

  return (
    <Stack.Navigator>
      <Stack.Group>
        <Stack.Screen
          name="Subscription"
          component={SubscriptionStack}
          options={{ headerShown: false }}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
};

const UnauthenticatedScreens = ({ showWarning = false, children }) => {
  return (
    <>
      {showWarning && <WebAccessDialog />}
      <Stack.Navigator>
        <Stack.Group screenOptions={{ headerShown: false }}>
          {/*Typically, for landing pages and register/login screens*/}
          <Stack.Screen name="Login" component={Login} />
          <Stack.Screen name="Forgot Password" component={ForgotPassword} />
          <Stack.Screen name="Join Company" component={CreateAccount} />
          <Stack.Screen name="Create Company" component={CreateCompany} />
        </Stack.Group>
        {children}
      </Stack.Navigator>
    </>
  );
};

const WebAccessDialog = () => {
  return (
    <Dialog
      title="This user does not have access to the Web app."
      visible
      actionSlot={[
        <Button
          key="close"
          mode="outlined"
          color="dark"
          onPress={() => {
            logOut();
            window.location.reload();
          }}>
          Close
        </Button>,
      ]}>
      <Text>
        Please login to the Mobile app or login with a different user.
      </Text>
    </Dialog>
  );
};
